import React from "react";
import {
  Building2,
  Phone,
  Mail,
  MapPin,
  Save,
  RefreshCw,
  FileText,
  Shield,
  Edit3,
  X,
  Image as ImageIcon,
} from "lucide-react";
import Button from "../../../../components/common/Button";
import { Input } from "../../../../components/common/Input";
import { CompanyInfoData } from "./types";

export interface CompanyInfoTabProps {
  companyData: CompanyInfoData | null;
  loadingInfo: boolean;
  savingInfo: boolean;
  isEditingInfo: boolean;
  setIsEditingInfo: (editing: boolean) => void;
  companyName: string;
  setCompanyName: (val: string) => void;
  companyAddress: string;
  setCompanyAddress: (val: string) => void;
  telephone: string;
  setTelephone: (val: string) => void;
  telephone2: string;
  setTelephone2: (val: string) => void;
  telephone3: string;
  setTelephone3: (val: string) => void;
  email: string;
  setEmail: (val: string) => void;
  email2: string;
  setEmail2: (val: string) => void;
  email3: string;
  setEmail3: (val: string) => void;
  aboutCompany: string;
  setAboutCompany: (val: string) => void;
  ceoStatement: string;
  setCeoStatement: (val: string) => void;
  returnPolicy: string;
  setReturnPolicy: (val: string) => void;
  termsAndConditions: string;
  setTermsAndConditions: (val: string) => void;
  privacyPolicy: string;
  setPrivacyPolicy: (val: string) => void;
  logoFile: File | null;
  setLogoFile: (file: File | null) => void;
  logoPreview: string | null;
  setLogoPreview: (preview: string | null) => void;
  handleSaveCompanyInfo: (e: React.FormEvent) => void;
  onOpenMediaModalForLogo: () => void;
}

export const CompanyInfoTab: React.FC<CompanyInfoTabProps> = ({
  companyData,
  loadingInfo,
  savingInfo,
  isEditingInfo,
  setIsEditingInfo,
  companyName,
  setCompanyName,
  companyAddress,
  setCompanyAddress,
  telephone,
  setTelephone,
  telephone2,
  setTelephone2,
  telephone3,
  setTelephone3,
  email,
  setEmail,
  email2,
  setEmail2,
  email3,
  setEmail3,
  aboutCompany,
  setAboutCompany,
  ceoStatement,
  setCeoStatement,
  returnPolicy,
  setReturnPolicy,
  termsAndConditions,
  setTermsAndConditions,
  privacyPolicy,
  setPrivacyPolicy,
  logoFile,
  setLogoFile,
  logoPreview,
  setLogoPreview,
  handleSaveCompanyInfo,
  onOpenMediaModalForLogo,
}) => {
  return (
    <div className="cht-space-y-6">
      <div className="cht-flex cht-items-center cht-justify-end cht-space-x-3">
        {!isEditingInfo ? (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setIsEditingInfo(true)}
            leftIcon={<Edit3 className="cht-h-4 cht-w-4" />}
            className="cht-rounded-xl cht-font-bold cht-text-xs"
          >
            Edit Company Profile
          </Button>
        ) : (
          <div className="cht-flex cht-items-center cht-space-x-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsEditingInfo(false)}
              leftIcon={<X className="cht-h-3.5 cht-w-3.5" />}
              className="cht-rounded-xl cht-font-bold cht-text-xs"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="company-info-edit-form"
              variant="primary"
              size="sm"
              disabled={savingInfo}
              isLoading={savingInfo}
              leftIcon={<Save className="cht-h-4 cht-w-4" />}
              className="cht-rounded-xl cht-font-bold cht-text-xs cht-min-w-[130px]"
            >
              {savingInfo ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        )}
      </div>

      {loadingInfo ? (
        <div className="cht-bg-white cht-border cht-border-gray-100 cht-shadow-sm cht-rounded-[24px] cht-p-12 cht-text-center cht-text-xs cht-text-gray-500">
          <RefreshCw className="cht-h-6 cht-w-6 cht-text-[#3e57da] cht-animate-spin cht-mx-auto cht-mb-2" />
          Loading company profile and configuration...
        </div>
      ) : (
        <form
          id="company-info-edit-form"
          onSubmit={handleSaveCompanyInfo}
          className="cht-space-y-6 cht-text-xs"
        >
          {/* Header Card with Logo */}
          <div className="cht-bg-gradient-to-r cht-from-[#1e293b] cht-via-indigo-950 cht-to-[#1e293b] cht-text-white cht-rounded-[24px] cht-p-6 md:cht-p-8 cht-shadow-md cht-border cht-border-indigo-900/40">
            <div className="cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-gap-6">
              <div className="cht-flex cht-items-center cht-space-x-5">
                <div className="cht-h-20 cht-w-20 cht-rounded-2xl cht-bg-white/10 cht-border cht-border-white/20 cht-p-2 cht-flex cht-items-center cht-justify-center cht-shrink-0 cht-overflow-hidden cht-relative">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo"
                      className="cht-max-h-full cht-max-w-full cht-object-contain"
                    />
                  ) : (
                    <Building2 className="cht-h-10 cht-w-10 cht-text-indigo-300" />
                  )}
                </div>

                <div className="cht-space-y-1.5">
                  <span className="cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-text-indigo-300 cht-block">
                    Corporate Identity
                  </span>
                  {!isEditingInfo ? (
                    <h2 className="cht-text-2xl cht-font-black cht-text-white cht-tracking-tight">
                      {companyData?.company_name || companyName || "Codehub Technologies LTD"}
                    </h2>
                  ) : (
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Company Name"
                      className="cht-h-10 cht-w-full cht-bg-white/10 cht-border cht-border-white/20 cht-text-white cht-placeholder-white/40 cht-text-sm cht-font-bold cht-rounded-xl cht-px-3 cht-outline-none focus:cht-border-white"
                      required
                    />
                  )}
                  <p className="cht-text-xs cht-text-slate-300 cht-flex cht-items-center cht-gap-1.5 cht-font-medium">
                    <MapPin className="cht-h-3.5 cht-w-3.5 cht-text-indigo-400 cht-shrink-0" />
                    <span>{companyData?.company_address || "Headquarters address not set"}</span>
                  </p>
                </div>
              </div>

              {isEditingInfo && (
                <div className="cht-shrink-0 cht-bg-white/10 cht-p-3 cht-rounded-2xl cht-border cht-border-white/20 cht-text-center">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={onOpenMediaModalForLogo}
                    leftIcon={<ImageIcon className="cht-h-4 cht-w-4" />}
                    className="cht-w-full cht-font-bold cht-text-xs cht-rounded-xl"
                  >
                    Select Logo from Media Library
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="cht-grid cht-grid-cols-1 lg:cht-grid-cols-12 cht-gap-6">
            {/* Left Column: Office & Contact */}
            <div className="lg:cht-col-span-6 cht-space-y-6">
              {/* Address Card */}
              <div className="cht-bg-white cht-border cht-border-gray-100 cht-shadow-sm cht-rounded-[24px] cht-p-6 cht-space-y-3">
                <div className="cht-flex cht-items-center cht-space-x-2 cht-border-b cht-border-gray-100 cht-pb-3">
                  <MapPin className="cht-h-5 cht-w-5 cht-text-[#3e57da]" />
                  <h3 className="cht-text-sm cht-font-black cht-text-[#1e293b]">Headquarters Address</h3>
                </div>
                {!isEditingInfo ? (
                  <div className="cht-p-3.5 cht-bg-gray-50/80 cht-rounded-xl cht-border cht-border-gray-100 cht-text-xs cht-font-medium cht-text-gray-800">
                    {companyData?.company_address || "No headquarters address specified."}
                  </div>
                ) : (
                  <Input
                    multiline
                    rows={2}
                    value={companyAddress}
                    onChange={(e) => setCompanyAddress(e.target.value)}
                    placeholder="Enter corporate office address..."
                  />
                )}
              </div>

              {/* Telephone Contact Lines */}
              <div className="cht-bg-white cht-border cht-border-gray-100 cht-shadow-sm cht-rounded-[24px] cht-p-6 cht-space-y-4">
                <div className="cht-flex cht-items-center cht-space-x-2 cht-border-b cht-border-gray-100 cht-pb-3">
                  <Phone className="cht-h-5 cht-w-5 cht-text-[#3e57da]" />
                  <h3 className="cht-text-sm cht-font-black cht-text-[#1e293b]">Corporate Phone Lines</h3>
                </div>
                {!isEditingInfo ? (
                  <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-3 cht-gap-3">
                    <div className="cht-p-3 cht-bg-gray-50/80 cht-border cht-border-gray-100 cht-rounded-xl cht-space-y-1">
                      <span className="cht-text-[10px] cht-font-black cht-uppercase cht-text-gray-400 cht-block">Primary Line</span>
                      <span className="cht-font-mono cht-text-xs cht-font-bold cht-text-[#1e293b] cht-block">{companyData?.telephone || "N/A"}</span>
                    </div>
                    <div className="cht-p-3 cht-bg-gray-50/80 cht-border cht-border-gray-100 cht-rounded-xl cht-space-y-1">
                      <span className="cht-text-[10px] cht-font-black cht-uppercase cht-text-gray-400 cht-block">Secondary Line</span>
                      <span className="cht-font-mono cht-text-xs cht-font-bold cht-text-[#1e293b] cht-block">{companyData?.telephone_2 || "N/A"}</span>
                    </div>
                    <div className="cht-p-3 cht-bg-gray-50/80 cht-border cht-border-gray-100 cht-rounded-xl cht-space-y-1">
                      <span className="cht-text-[10px] cht-font-black cht-uppercase cht-text-gray-400 cht-block">Hotline / Support</span>
                      <span className="cht-font-mono cht-text-xs cht-font-bold cht-text-[#1e293b] cht-block">{companyData?.telephone_3 || "N/A"}</span>
                    </div>
                  </div>
                ) : (
                  <div className="cht-space-y-3">
                    <Input
                      type="text"
                      value={telephone}
                      onChange={(e) => setTelephone(e.target.value)}
                      placeholder="Primary Phone Number"
                      leftIcon={<Phone className="cht-h-4 cht-w-4 cht-text-gray-400" />}
                    />
                    <div className="cht-grid cht-grid-cols-2 cht-gap-3">
                      <Input
                        type="text"
                        value={telephone2}
                        onChange={(e) => setTelephone2(e.target.value)}
                        placeholder="Secondary Phone"
                        leftIcon={<Phone className="cht-h-4 cht-w-4 cht-text-gray-400" />}
                      />
                      <Input
                        type="text"
                        value={telephone3}
                        onChange={(e) => setTelephone3(e.target.value)}
                        placeholder="Hotline / Support"
                        leftIcon={<Phone className="cht-h-4 cht-w-4 cht-text-gray-400" />}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Corporate Emails Card */}
              <div className="cht-bg-white cht-border cht-border-gray-100 cht-shadow-sm cht-rounded-[24px] cht-p-6 cht-space-y-4">
                <div className="cht-flex cht-items-center cht-space-x-2 cht-border-b cht-border-gray-100 cht-pb-3">
                  <Mail className="cht-h-5 cht-w-5 cht-text-[#3e57da]" />
                  <h3 className="cht-text-sm cht-font-black cht-text-[#1e293b]">Corporate Email Channels</h3>
                </div>
                {!isEditingInfo ? (
                  <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-3 cht-gap-3">
                    <div className="cht-p-3 cht-bg-gray-50/80 cht-border cht-border-gray-100 cht-rounded-xl cht-space-y-1">
                      <span className="cht-text-[10px] cht-font-black cht-uppercase cht-text-gray-400 cht-block">Primary Email</span>
                      <span className="cht-font-mono cht-text-xs cht-font-bold cht-text-[#1e293b] cht-truncate cht-block">{companyData?.email || "N/A"}</span>
                    </div>
                    <div className="cht-p-3 cht-bg-gray-50/80 cht-border cht-border-gray-100 cht-rounded-xl cht-space-y-1">
                      <span className="cht-text-[10px] cht-font-black cht-uppercase cht-text-gray-400 cht-block">Support Email</span>
                      <span className="cht-font-mono cht-text-xs cht-font-bold cht-text-[#1e293b] cht-truncate cht-block">{companyData?.email_2 || "N/A"}</span>
                    </div>
                    <div className="cht-p-3 cht-bg-gray-50/80 cht-border cht-border-gray-100 cht-rounded-xl cht-space-y-1">
                      <span className="cht-text-[10px] cht-font-black cht-uppercase cht-text-gray-400 cht-block">Billing Email</span>
                      <span className="cht-font-mono cht-text-xs cht-font-bold cht-text-[#1e293b] cht-truncate cht-block">{companyData?.email_3 || "N/A"}</span>
                    </div>
                  </div>
                ) : (
                  <div className="cht-space-y-3">
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Primary Email"
                      leftIcon={<Mail className="cht-h-4 cht-w-4 cht-text-gray-400" />}
                    />
                    <div className="cht-grid cht-grid-cols-2 cht-gap-3">
                      <Input
                        type="email"
                        value={email2}
                        onChange={(e) => setEmail2(e.target.value)}
                        placeholder="Support Email"
                        leftIcon={<Mail className="cht-h-4 cht-w-4 cht-text-gray-400" />}
                      />
                      <Input
                        type="email"
                        value={email3}
                        onChange={(e) => setEmail3(e.target.value)}
                        placeholder="Billing Email"
                        leftIcon={<Mail className="cht-h-4 cht-w-4 cht-text-gray-400" />}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Mission & Policies */}
            <div className="lg:cht-col-span-6 cht-space-y-6">
              {/* About & CEO Statement */}
              <div className="cht-bg-white cht-border cht-border-gray-100 cht-shadow-sm cht-rounded-[24px] cht-p-6 cht-space-y-4">
                <div className="cht-flex cht-items-center cht-space-x-2 cht-border-b cht-border-gray-100 cht-pb-3">
                  <FileText className="cht-h-5 cht-w-5 cht-text-[#3e57da]" />
                  <h3 className="cht-text-sm cht-font-black cht-text-[#1e293b]">About & CEO Statement</h3>
                </div>
                {!isEditingInfo ? (
                  <div className="cht-space-y-3">
                    <div className="cht-p-3.5 cht-bg-gray-50/80 cht-rounded-xl cht-border cht-border-gray-100">
                      <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-block">About Company</span>
                      <p className="cht-text-xs cht-text-gray-700 cht-mt-1 cht-leading-relaxed">{companyData?.about_company || "No company description provided."}</p>
                    </div>
                    <div className="cht-p-3.5 cht-bg-indigo-50/40 cht-border cht-border-indigo-100 cht-rounded-xl">
                      <span className="cht-text-[10px] cht-font-black cht-text-indigo-800 cht-uppercase cht-block">CEO Statement</span>
                      <p className="cht-text-xs cht-text-indigo-950 cht-mt-1 cht-leading-relaxed">{companyData?.ceo_statment || "No CEO statement provided."}</p>
                    </div>
                  </div>
                ) : (
                  <div className="cht-space-y-3">
                    <Input
                      multiline
                      rows={3}
                      value={aboutCompany}
                      onChange={(e) => setAboutCompany(e.target.value)}
                      placeholder="About Codehub Technologies..."
                      label="About Company"
                    />
                    <Input
                      multiline
                      rows={3}
                      value={ceoStatement}
                      onChange={(e) => setCeoStatement(e.target.value)}
                      placeholder="CEO Executive Statement..."
                      label="CEO Statement"
                    />
                  </div>
                )}
              </div>

              {/* Legal Governance & Policies */}
              <div className="cht-bg-white cht-border cht-border-gray-100 cht-shadow-sm cht-rounded-[24px] cht-p-6 cht-space-y-4">
                <div className="cht-flex cht-items-center cht-space-x-2 cht-border-b cht-border-gray-100 cht-pb-3">
                  <Shield className="cht-h-5 cht-w-5 cht-text-[#3e57da]" />
                  <h3 className="cht-text-sm cht-font-black cht-text-[#1e293b]">Legal Governance & Policies</h3>
                </div>
                {!isEditingInfo ? (
                  <div className="cht-space-y-3">
                    <div className="cht-p-3.5 cht-bg-gray-50/80 cht-rounded-xl cht-border cht-border-gray-100">
                      <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-block">Terms & Conditions</span>
                      <p className="cht-font-mono cht-text-[11px] cht-text-gray-700 cht-mt-1 cht-line-clamp-3">{companyData?.term_and_conditions || "Not set"}</p>
                    </div>
                    <div className="cht-p-3.5 cht-bg-gray-50/80 cht-rounded-xl cht-border cht-border-gray-100">
                      <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-block">Privacy Policy</span>
                      <p className="cht-font-mono cht-text-[11px] cht-text-gray-700 cht-mt-1 cht-line-clamp-3">{companyData?.privacy_policy || "Not set"}</p>
                    </div>
                    <div className="cht-p-3.5 cht-bg-gray-50/80 cht-rounded-xl cht-border cht-border-gray-100">
                      <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-block">Return & Refund Policy</span>
                      <p className="cht-font-mono cht-text-[11px] cht-text-gray-700 cht-mt-1 cht-line-clamp-3">{companyData?.return_policy || "Not set"}</p>
                    </div>
                  </div>
                ) : (
                  <div className="cht-space-y-3">
                    <Input
                      multiline
                      rows={3}
                      value={termsAndConditions}
                      onChange={(e) => setTermsAndConditions(e.target.value)}
                      placeholder="Terms and Conditions legal text..."
                      label="Terms & Conditions"
                      inputClassName="cht-font-mono"
                    />
                    <Input
                      multiline
                      rows={3}
                      value={privacyPolicy}
                      onChange={(e) => setPrivacyPolicy(e.target.value)}
                      placeholder="Privacy Policy legal text..."
                      label="Privacy Policy"
                      inputClassName="cht-font-mono"
                    />
                    <Input
                      multiline
                      rows={3}
                      value={returnPolicy}
                      onChange={(e) => setReturnPolicy(e.target.value)}
                      placeholder="Return and Refund policy..."
                      label="Return Policy"
                      inputClassName="cht-font-mono"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};
